import { EPS } from './numbers.js';
import { dot, rotate, sub, type Vec2 } from './vec.js';
import { localToWorld } from './transform.js';

export interface Aabb2 {
  readonly min: Vec2;
  readonly max: Vec2;
}

export function aabbFromPoints(points: readonly Vec2[]): Aabb2 | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

export function aabbUnion(boxes: readonly (Aabb2 | null)[]): Aabb2 | null {
  const valid = boxes.filter((b): b is Aabb2 => b !== null);
  return aabbFromPoints(valid.flatMap((b) => [b.min, b.max]));
}

export const aabbSize = (b: Aabb2): Vec2 => ({ x: b.max.x - b.min.x, y: b.max.y - b.min.y });
export const aabbCenter = (b: Aabb2): Vec2 => ({
  x: (b.min.x + b.max.x) / 2,
  y: (b.min.y + b.max.y) / 2,
});

export function aabbExpand(b: Aabb2, margin: number): Aabb2 {
  return {
    min: { x: b.min.x - margin, y: b.min.y - margin },
    max: { x: b.max.x + margin, y: b.max.y + margin },
  };
}

/** Strict overlap: boxes that only touch along an edge do not intersect. */
export function aabbIntersects(a: Aabb2, b: Aabb2): boolean {
  return (
    a.min.x < b.max.x - EPS &&
    a.max.x > b.min.x + EPS &&
    a.min.y < b.max.y - EPS &&
    a.max.y > b.min.y + EPS
  );
}

/**
 * Oriented rectangle in the ground plane. `width` is along the local x axis,
 * `depth` along the local y axis, both centered on `center`.
 */
export interface Obb2 {
  readonly center: Vec2;
  readonly width: number;
  readonly depth: number;
  readonly rotation: number;
}

/** Corners in counter-clockwise order. */
export function obbCorners(o: Obb2): Vec2[] {
  const hw = o.width / 2;
  const hd = o.depth / 2;
  const placement = { position: o.center, rotation: o.rotation };
  return [
    { x: -hw, y: -hd },
    { x: hw, y: -hd },
    { x: hw, y: hd },
    { x: -hw, y: hd },
  ].map((p) => localToWorld(p, placement));
}

export function obbToAabb(o: Obb2): Aabb2 {
  return aabbFromPoints(obbCorners(o)) as Aabb2;
}

function projectOnAxis(points: readonly Vec2[], axis: Vec2): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const d = dot(p, axis);
    min = Math.min(min, d);
    max = Math.max(max, d);
  }
  return [min, max];
}

/**
 * Separating axis test for two rectangles. Touching edges are not reported as
 * intersection, so objects placed flush against each other are valid.
 */
export function obbIntersects(a: Obb2, b: Obb2): boolean {
  const ca = obbCorners(a);
  const cb = obbCorners(b);
  const axes = [
    rotate({ x: 1, y: 0 }, a.rotation),
    rotate({ x: 0, y: 1 }, a.rotation),
    rotate({ x: 1, y: 0 }, b.rotation),
    rotate({ x: 0, y: 1 }, b.rotation),
  ];
  for (const axis of axes) {
    const [minA, maxA] = projectOnAxis(ca, axis);
    const [minB, maxB] = projectOnAxis(cb, axis);
    if (maxA <= minB + EPS || maxB <= minA + EPS) return false;
  }
  return true;
}

/** Rectangle occupied by a straight wall segment of given thickness. */
export function wallFootprint(start: Vec2, end: Vec2, thickness: number): Obb2 {
  const dir = sub(end, start);
  const len = Math.hypot(dir.x, dir.y);
  const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const rotation = Math.atan2(dir.y, dir.x);
  return { center, width: len, depth: thickness, rotation };
}
